import { Copy, ArrowDown, ArrowUp, MoreHorizontal, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export function ItemCard({
  index,
  title,
  summary,
  aside,
  onEdit,
  onDuplicate,
  onRemove,
  onMoveUp,
  onMoveDown,
  mobile = false,
}: {
  index: number;
  title: string;
  summary?: string;
  aside?: string;
  onEdit: () => void;
  onDuplicate?: () => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  mobile?: boolean;
}) {
  const { t } = useTranslation("prescriptions");
  return (
    <div
      className={cn(
        "flex items-start gap-3 border border-border/70 bg-card",
        mobile ? "rounded-2xl p-3.5 pr-2.5" : "rounded-[14px] py-3.5 pl-3.5 pr-3",
      )}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent text-[13px] font-bold text-accent-foreground">
        {index + 1}
      </span>
      <button
        type="button"
        onClick={mobile ? onEdit : undefined}
        className={cn("min-w-0 flex-1 text-left", !mobile && "cursor-default")}
        tabIndex={mobile ? 0 : -1}
      >
        <div className="flex items-baseline gap-3">
          <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">{title}</span>
          {aside ? <span className="shrink-0 text-[13px] text-muted-foreground">{aside}</span> : null}
        </div>
        {summary ? <p className="mt-1 whitespace-pre-line text-[13.5px] text-muted-foreground">{summary}</p> : null}
      </button>
      {!mobile ? (
        <Button type="button" variant="outline" className="h-9 rounded-[9px] px-3 text-[13px]" onClick={onEdit}>
          {t("editor.item.edit")}
        </Button>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={mobile ? "h-11 w-11" : "h-9 w-9"}
            aria-label={t("editor.item.moreActions", { index: index + 1 })}
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onDuplicate ? (
            <DropdownMenuItem onSelect={onDuplicate}>
              <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("editor.item.duplicate")}
            </DropdownMenuItem>
          ) : null}
          {onMoveUp ? (
            <DropdownMenuItem onSelect={onMoveUp}>
              <ArrowUp className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("editor.item.moveUp")}
            </DropdownMenuItem>
          ) : null}
          {onMoveDown ? (
            <DropdownMenuItem onSelect={onMoveDown}>
              <ArrowDown className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("editor.item.moveDown")}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onRemove} className="text-destructive focus:text-destructive">
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("editor.item.remove")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
